import axios from '@api';

import { transformCreateSurvey, transformSurvey, transformSurveyList } from './utilities/helpers';

/**
 * complete survey stripe checkout
 * @param {Object} options
 * @param {String} options.token
 * @return {Promise}
 */
export const completeSurveyStripeCheckout = ({ token }) => {
  return axios({
    data: {
      token,
    },
    method: 'post',
    url: '/api/surveys/complete_stripe_checkout',
    withCredentials: true,
  });
};

/**
 * create atlas survey
 * @param {Object} options
 * @param {String} [options.askWhyDocumentId]
 * @param {Number} [options.askWhyJobId]
 * @param {String} [options.askWhyPeopleGroupTypeId]
 * @param {Number} options.atlasInstantSurveyRef
 * @param {String} [options.cancelUrl]
 * @param {Number} [options.id] if an id is provided, it will act like an update call
 * @param {String} [options.paymentMethod]
 * @param {String} [options.successUrl]
 * @param {String} [options.status]
 * @param {String} options.title
 * @return {Promise}
 */
export const createAtlasSurvey = async ({
  askWhyDocumentId: ask_why_document_id,
  askWhyJobId: ask_why_job_id,
  askWhyPeopleGroupResponseId: ask_why_people_group_id,
  askWhyPeopleGroupTypeId: ask_why_people_group_type_id,
  atlasInstantSurveyRef: atlas_instant_survey_ref,
  cancelUrl: cancel_url,
  id,
  paymentMethod: payment_method,
  status,
  successUrl: success_url,
  title,
}) => {
  return axios({
    data: {
      ask_why_document_id,
      ask_why_job_id,
      ask_why_people_group_id,
      ask_why_people_group_type_id,
      atlas_instant_survey_ref,
      cancel_url,
      id,
      payment_method,
      status,
      success_url,
      title,
    },
    method: 'post',
    url: '/api/surveys',
    withCredentials: true,
  }).then(({ data }) => transformCreateSurvey(data));
};

/**
 * pause survey
 * @param {Object} options
 * @param {Number} options.id
 * @param {String[]} [options.reasons]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise}
 */
export const pauseSurvey = ({ id, reasons = [], signal }) => {
  return axios({
    data: {
      reason: reasons.map((reason) => reason.replaceAll(',', '\\,')).join(','),
    },
    method: 'put',
    signal,
    url: `/api/surveys/${id}/pause`,
    withCredentials: true,
  });
};

/**
 * publish atlas survey
 * @param {Object} options
 * @param {Array} options.addons
 * @param {Number} options.atlasInstantSurveyRef
 * @param {String} options.id
 * @param {String} [options.otherRequests]
 * @param {String} [options.payment_method]
 * @param {String} [options.status]
 * @param {String} options.title
 * @param {Boolean} [options.use_promo_credits]
 */
export const publishAtlasSurvey = async ({
  addons,
  atlasInstantSurveyRef: atlas_instant_survey_ref,
  id,
  other_requests,
  payment_method,
  status = null,
  title,
  use_promo_credits,
}) => {
  return axios({
    data: {
      addons,
      atlas_instant_survey_ref,
      id,
      other_requests,
      payment_method,
      status,
      title,
      use_promo_credits,
    },
    method: 'post',
    url: '/api/surveys',
    withCredentials: true,
  }).then(({ data }) => transformCreateSurvey(data));
};

/**
 * read survey
 * @param {Object} options
 * @param {String} options.id
 * @param {String} options.publicToken
 * @return {Promise}
 */
export const readSurvey = ({ id, publicToken }) => {
  // Bypass sending cookies when using publicToken
  const withCredentials = publicToken ? false : true;

  return axios({
    method: 'get',
    params: {
      publicToken,
    },
    url: `/api/surveys/${id}`,
    withCredentials,
  }).then(({ data }) => transformSurvey(data));
};

/**
 * read survey drafts — thin wrapper over readSurveys that always merges the
 * `draft` tag into the request and exposes the resulting list under a
 * `folders` key so the response drops into ListPage's `readFolders` contract
 * unchanged. The original `items` array is preserved alongside `folders`.
 * @param {Object} [options]
 * @param {String} [options.order]
 * @param {Number} [options.page]
 * @param {String} [options.query]
 * @param {AbortSignal} [options.signal]
 * @param {Number} [options.size]
 * @param {String} [options.sortBy]
 * @param {String[]} [options.tags] additional status/creator tags; `'draft'` is always included
 * @return {Promise<import('axios').AxiosResponse<ReturnType<typeof transformSurveyList> & { folders: ReturnType<typeof transformSurveyList>['items'] }>>}
 */
export const readSurveyDrafts = async ({
  order,
  page,
  query,
  signal,
  size,
  sortBy,
  tags = [],
} = {}) => {
  const mergedTags = Array.from(new Set([...tags, 'draft']));
  const response = await readSurveys({
    order,
    page,
    query,
    signal,
    size,
    sortBy,
    tags: mergedTags,
  });
  return {
    ...response,
    data: {
      ...response?.data,
      folders: response?.data?.items,
    },
  };
};

/**
 * read surveys
 * @param {Object} options
 * @param {String} [options.order]
 * @param {Number} [options.page]
 * @param {String} [options.query]
 * @param {AbortSignal} [options.signal]
 * @param {Number} [options.size]
 * @param {String} [options.sortBy]
 * @param {Array} [options.tags]
 * @return {Promise}
 */
export const readSurveys = async ({
  order: order_by,
  page,
  query,
  signal,
  size,
  sortBy: sort_by,
  tags = [],
}) => {
  return axios({
    method: 'get',
    params: {
      ...(order_by && { order_by }),
      ...(page > 1 && { page }),
      ...(query && { query }),
      ...(size && { size }),
      ...(sort_by && { sort_by }),
      ...(tags.length > 0 && { tags: tags.map((tag) => tag.replace(/^is_/, '')).join() }),
    },
    signal,
    transformResponse: [...axios.defaults.transformResponse, (data) => transformSurveyList(data)],
    url: '/v2/api/survey/',
    withCredentials: true,
  });
};

/**
 * unpause survey
 * @param {Object} options
 * @param {Number} options.id
 * @param {AbortSignal} [options.signal]
 * @returns {Promise}
 */
export const unpauseSurvey = ({ id, signal }) => {
  return axios({
    method: 'put',
    signal,
    url: `/api/surveys/${id}/unpause`,
    withCredentials: true,
  });
};

/**
 * update survey
 * @param {Object} options
 * @param {String} options.id
 * @param {Object} options.survey
 * @return {Promise}
 */
export const updateSurvey = ({ id, ...survey }) => {
  return axios({
    data: survey,
    method: 'put',
    url: `/api/surveys/${id}`,
    withCredentials: true,
  });
};

/**
 * update survey title
 * @param {Object} options
 * @param {String} options.id
 * @param {String} options.name
 * @return {Promise}
 */
export const updateSurveyTitle = ({ id, name: title }) => {
  return axios({
    data: { title },
    method: 'put',
    url: `/api/surveys/${id}/rename`,
    withCredentials: true,
  });
};

/**
 * delete surveys
 * @param {Object} options
 * @param {Array} options.ids
 * @return {Promise}
 */
export const deleteSurveys = ({ ids }) => {
  return Promise.all(
    ids.map((id) =>
      axios({
        method: 'delete',
        url: `/api/surveys/${id}`,
        withCredentials: true,
      })
    )
  );
};

/**
 * reads survey share token for read only
 * @param {Number} options.surveyId
 * @return {Promise}
 */
export const readSurveyShareToken = ({ surveyId }) => {
  return axios({
    method: 'put',
    transformResponse: [
      (data) => {
        const response = JSON.parse(data);
        return response?.publicToken;
      },
    ],
    url: `/api/surveys/${surveyId}/share`,
    withCredentials: true,
  });
};

/**
 * reads survey public token and retrieves survey id
 * @param  {String} options.publicToken
 * @return {Promise}
 */
export const readSurveyPublicToken = async ({ publicToken }) => {
  // Bypass sending cookies when using publicToken
  const withCredentials = publicToken ? false : true;

  return axios({
    method: 'get',
    transformResponse: [
      (data) => {
        const response = JSON.parse(data);
        return {
          atlasInstantSurveyRef: response?.atlas_instant_survey_ref,
          instantSurveyTypeId: response?.instant_survey_type_id,
          serviceToken: response?.service_token,
          surveyId: response?.surveyId,
        };
      },
    ],
    url: `/api/surveys/check-token/${publicToken}`,
    withCredentials,
  });
};

/**
 * shares instant survey with all users under a client
 * @param {Object} options
 * @param  {Number} options.clientId
 * @param  {Array} options.ids
 * @return {Promise}
 */
export const shareInstantSurveyWithClientGroup = ({ clientId, ids }) => {
  return axios({
    data: {
      instant_survey_ids: ids,
      recipient_client_id: clientId,
    },
    method: 'post',
    url: '/v2/api/survey/client_share',
    withCredentials: true,
  });
};

/**
 * shares instant survey with users
 * @param {Object} options
 * @param  {Array} options.ids
 * @param  {Boolean} [options.skipEmail]
 * @param  {Array} options.userIds
 * @return {Promise}
 */
export const shareInstantSurveyWithUsers = ({ ids, skipEmail = false, userIds }) => {
  return axios({
    data: {
      instant_survey_ids: ids,
      recipient_ids: userIds,
      skip_email: skipEmail,
    },
    method: 'post',
    url: '/v2/api/survey/user_share',
    withCredentials: true,
  });
};
